import { Button, Card, CardContent } from '@bs/ui-components'
import { Check, CheckCircle2, ChevronDown, CircleSlash2, Clock3 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { availabilityOptions, type AvailabilityId } from '@/features/profile/types'
import {
  AVAILABILITY_TO_STATUS,
  STATUS_TO_AVAILABILITY,
  getMyJobPreferences,
  updateMyJobPreferences,
} from '@/services/job-preferences'

const availabilityAppearance = {
  2: { icon: CheckCircle2, className: 'bg-[#e9f9f2] text-[#00895b]' },
  1: { icon: Clock3, className: 'bg-[#fff7e8] text-[#a66200]' },
  0: { icon: CircleSlash2, className: 'bg-[#fff0f0] text-destructive' },
} satisfies Record<AvailabilityId, { icon: typeof CheckCircle2; className: string }>

/**
 * Self-contained — fetches its own preferences:
 * this card is rendered on several pages (profile, jobs, job detail) that otherwise have no
 * reason to load job preferences, so it fetches its own rather than requiring every render site
 * to thread a prop through. `job_preferences.status` IS this widget's real backing — there was
 * previously a SEPARATE mock-only `availability` concept that never touched the real field at
 * all, which is the exact "two availabilities can drift" bug this replaces.
 */
export function AvailabilityCard() {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const [realAvailability, setRealAvailability] = useState<AvailabilityId>(0)
  const [loadError, setLoadError] = useState('')

  useEffect(() => {
    let cancelled = false
    getMyJobPreferences()
      .then(({ preferences }) => {
        if (cancelled) return
        setRealAvailability(STATUS_TO_AVAILABILITY[preferences.status])
      })
      .catch(() => {
        // The widget stays on its default rather than inventing a status, but say the load failed
        // — a defaulted "availability" presented as fact misstates the candidate to themselves.
        if (!cancelled) setLoadError("Couldn't load your availability — showing the default.")
      })
    return () => {
      cancelled = true
    }
  }, [])

  const availability = realAvailability

  async function changeAvailability(id: AvailabilityId) {
    {
      setRealAvailability(id) // optimistic — this widget has no error banner to hold a rollback for
      try {
        await updateMyJobPreferences({ status: AVAILABILITY_TO_STATUS[id] })
        setLoadError('') // a successful write makes the shown value real — the load notice is stale
      } catch {
        getMyJobPreferences()
          .then(({ preferences }) => setRealAvailability(STATUS_TO_AVAILABILITY[preferences.status]))
          .catch(() => {})
      }
      return
    }
  }

  const selected = availabilityOptions.find((option) => option.id === availability)!
  const appearance = availabilityAppearance[availability]
  const StatusIcon = appearance.icon

  useEffect(() => {
    if (!open) return
    function close(event: PointerEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', close)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', close)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [open])

  return (
    <Card className="overflow-visible">
      <CardContent className="p-5">
        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          Availability
        </p>
        {loadError ? <p role="alert" className="mt-2 text-xs text-destructive">{loadError}</p> : null}
        <div ref={containerRef} className="relative mt-3">
          <Button
            type="button"
            variant="ghost"
            onClick={() => setOpen((value) => !value)}
            aria-haspopup="listbox"
            aria-expanded={open}
            className={`h-auto w-full justify-start rounded-xl px-3 py-3 ${appearance.className}`}
          >
            <StatusIcon className="size-5 shrink-0" />
            <span className="flex-1 text-left font-semibold">{selected.label}</span>
            <ChevronDown className={`size-4 transition ${open ? 'rotate-180' : ''}`} />
          </Button>

          {open ? (
            <div
              role="listbox"
              aria-label="Change availability"
              className="absolute inset-x-0 top-[calc(100%+8px)] z-30 overflow-hidden rounded-xl border border-border bg-white p-1.5 shadow-[0_14px_35px_rgba(0,7,79,0.16)]"
            >
              {availabilityOptions.map((option) => {
                const optionAppearance = availabilityAppearance[option.id]
                const OptionIcon = optionAppearance.icon
                return (
                  <button
                    key={option.id}
                    type="button"
                    role="option"
                    aria-selected={option.id === availability}
                    onClick={() => {
                      void changeAvailability(option.id)
                      setOpen(false)
                    }}
                    className="flex w-full items-start gap-3 rounded-lg px-3 py-3 text-left transition hover:bg-muted"
                  >
                    <OptionIcon className={`mt-0.5 size-5 shrink-0 ${optionAppearance.className.split(' ')[1]}`} />
                    <span className="min-w-0 flex-1">
                      <strong className="block text-sm text-[#404040]">{option.label}</strong>
                      <span className="mt-0.5 block text-xs leading-5 text-muted-foreground">
                        {option.description}
                      </span>
                    </span>
                    {option.id === availability ? <Check className="mt-0.5 size-4 text-accent" /> : null}
                  </button>
                )
              })}
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  )
}
